import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import Icon from '../icon/Icon'
import Text from '../Text'
import Popover from '../Popover'
import { NewDot } from '../SecondaryNavigation'

import { useAppState } from '../../context/AppContext'

import { ICompanyTerm } from '../../common/models/Company'

interface IProps<T extends { name?: string }> {
  selectedTerm?: T
  compareTo?: T
  // Listed in this order
  termOptions: T[]
  // What tells two options apart
  keyOf: (term: T) => string | number
  allowCompare: boolean
  onSelectTerm: (term: T) => void
  onSetCompareTo: (term: T | undefined) => void
  // The heading of the list, and the popover's name: a term unless given
  label?: string
  ariaLabel?: string
  // Terms that hold something waiting on the viewer get a red dot in the list,
  // and the chip gets one too, so the closed filter already says there is one
  hasDot?: (term: T) => boolean
}

// Evaluation terms as they are listed: the newest first
export const newestTermsFirst = (terms: ICompanyTerm[]) =>
  [...terms].sort((a, b) => b.termNumber - a.termNumber)

// Chip + popover for choosing which period a view shows: an evaluation term, or a
// measurement of a survey. The popover lists all options (primary selection) and, when
// allowCompare is set, a toggle that reveals a second list to pick one to compare
// against. Selecting the active compare option as primary clears the comparison.
// Selection and compare state are lifted to the parent via the callbacks.
const TermFilter = <T extends { name?: string }>({
  selectedTerm,
  compareTo,
  termOptions,
  keyOf,
  allowCompare,
  onSelectTerm,
  onSetCompareTo,
  label,
  ariaLabel,
  hasDot,
}: IProps<T>): JSX.Element | null => {
  const { t } = useTranslation()
  const { theme } = useAppState()
  const chipRef = useRef<HTMLButtonElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [compareEnabled, setCompareEnabled] = useState<boolean>(!!compareTo)

  if (!selectedTerm || termOptions.length === 0) return null

  const isSame = (a?: T, b?: T) => !!a && !!b && keyOf(a) === keyOf(b)
  const compareOptions = termOptions.filter(
    (term) => !isSame(term, selectedTerm)
  )
  const hasAnyDot = !!hasDot && termOptions.some((term) => hasDot(term))

  // When dropdown opens, sync compareEnabled with current compareTo state
  const handleToggleOpen = () => {
    setIsOpen((prev) => {
      const next = !prev
      if (next) setCompareEnabled(!!compareTo)
      return next
    })
  }

  const handleClose = () => setIsOpen(false)

  const handlePickPrimary = (term: T) => {
    onSelectTerm(term)
    if (isSame(term, compareTo)) {
      onSetCompareTo(undefined)
    }
    handleClose()
  }

  const handlePickCompare = (term: T) => {
    onSetCompareTo(term)
    handleClose()
  }

  const handleToggleCompare = () => {
    if (compareEnabled) {
      setCompareEnabled(false)
      if (compareTo) onSetCompareTo(undefined)
    } else {
      if (compareOptions.length === 0) return
      setCompareEnabled(true)
    }
  }

  return (
    <Wrapper>
      <PrimaryChip
        ref={chipRef}
        type="button"
        onClick={handleToggleOpen}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        $open={isOpen}
      >
        <Icon type="Calendar" size={16} color={theme.colors.primary} />
        <Text textStyle="labelHeavy" color="textDark" tag="span">
          {selectedTerm.name}
        </Text>
        {hasAnyDot && <NewDot />}
        <Caret $open={isOpen}>
          <Icon type="ChevronDown" size={12} color={theme.colors.textLight} />
        </Caret>
      </PrimaryChip>

      <Popover
        anchor={chipRef.current}
        isOpen={isOpen}
        onClose={handleClose}
        placement="bottom-start"
        offset={8}
        ariaLabel={ariaLabel ?? t('userProfileTermReviews.selectTerm')}
      >
        <DropdownContent>
          <Section>
            <SectionLabel
              textStyle="labelSmallCaps"
              color="textLight"
              tag="span"
            >
              {label ?? t('trends.term')}
            </SectionLabel>
            <OptionList role="listbox">
              {termOptions.map((term) => {
                const active = isSame(term, selectedTerm)
                return (
                  <OptionRow
                    key={`primary-${keyOf(term)}`}
                    type="button"
                    role="option"
                    aria-selected={active}
                    $active={active}
                    onClick={() => handlePickPrimary(term)}
                  >
                    <Radio $active={active} />
                    <Text textStyle="label" color="textDark" tag="span">
                      {term.name}
                    </Text>
                    {hasDot?.(term) && <OptionDot />}
                  </OptionRow>
                )
              })}
            </OptionList>
          </Section>

          {allowCompare && (
            <>
              <Divider />
              <Section>
                <ToggleRow
                  type="button"
                  onClick={handleToggleCompare}
                  disabled={compareOptions.length === 0}
                  aria-pressed={compareEnabled}
                >
                  <Text textStyle="labelSmallCaps" color="textLight" tag="span">
                    {t('general.actions.compare')}
                  </Text>
                  <SwitchTrack $on={compareEnabled}>
                    <SwitchThumb $on={compareEnabled} />
                  </SwitchTrack>
                </ToggleRow>
                {compareOptions.length === 0 && (
                  <DisabledHint
                    textStyle="caption"
                    color="textLight"
                    tag="span"
                  >
                    {t('trends.compareDisabledNoOther')}
                  </DisabledHint>
                )}
                {compareEnabled && compareOptions.length > 0 && (
                  <CompareList role="listbox">
                    {compareOptions.map((term) => {
                      const active = isSame(term, compareTo)
                      return (
                        <OptionRow
                          key={`compare-${keyOf(term)}`}
                          type="button"
                          role="option"
                          aria-selected={active}
                          $active={active}
                          onClick={() => handlePickCompare(term)}
                        >
                          <Radio $active={active} />
                          <Text textStyle="label" color="textDark" tag="span">
                            {term.name}
                          </Text>
                        </OptionRow>
                      )
                    })}
                  </CompareList>
                )}
              </Section>
            </>
          )}
        </DropdownContent>
      </Popover>
    </Wrapper>
  )
}

export default TermFilter

const Wrapper = styled.div`
  display: inline-flex;
  align-items: center;
`

const PrimaryChip = styled.button<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  height: 32px;
  box-sizing: border-box;
  cursor: pointer;
  border: none;
  background: ${({ theme, $open }) =>
    $open ? theme.colors.primaryFadeLight : theme.colors.primaryLight};
  transition: background-color 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryFadeLight};
  }
`

const Caret = styled.span<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'none')};
  transition: transform 0.18s ease;

  > span {
    margin-right: 0;
  }
`

const DropdownContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 0;
  min-width: 260px;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const SectionLabel = styled(Text)`
  padding: 0 16px 4px;
`

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 240px;
  overflow-y: auto;
`

const CompareList = styled(OptionList)`
  margin-top: 4px;
`

const OptionRow = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;
  padding: 8px 16px;
  border: none;
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primaryFadeLight : 'transparent'};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryFadeLight};
  }
`

const Radio = styled.span<{ $active: boolean }>`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.primary : theme.colors.textLighter};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primary : 'transparent'};
  box-shadow: ${({ theme, $active }) =>
    $active ? `inset 0 0 0 2px ${theme.colors.lightest}` : 'none'};
  flex-shrink: 0;
  transition: background-color 0.15s ease, border-color 0.15s ease;
`

const OptionDot = styled(NewDot)`
  margin-left: auto;
`

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.textLighter};
  margin: 8px 0;
`

const ToggleRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  width: 100%;

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primaryFadeLight};
  }
`

const SwitchTrack = styled.span<{ $on: boolean }>`
  position: relative;
  width: 32px;
  height: 18px;
  border-radius: 999px;
  background: ${({ theme, $on }) =>
    $on ? theme.colors.primary : theme.colors.textLighter};
  transition: background-color 0.15s ease;
  flex-shrink: 0;
`

const SwitchThumb = styled.span<{ $on: boolean }>`
  position: absolute;
  top: 2px;
  left: ${({ $on }) => ($on ? '16px' : '2px')};
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.lightest};
  transition: left 0.15s ease;
`

const DisabledHint = styled(Text)`
  padding: 4px 16px 0;
`
